import { Text, Stack } from '@genial/components';
import { Spacing } from '@genial/design-tokens';
import { useNavigation } from '@react-navigation/native';
import { Pressable } from 'native-base';
import React from 'react';

import { TableContentItemStatus } from '../../../../components/TableContent/status';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { normalizeRedeemBatchTicketRequestsData } from '../../../../store/batchTicket/normalize';
import { useSpreadsheetRequestItem } from '../../hooks/spreadsheet-request-item';

import { DetailsButton } from './components/DetailsButton';
import { DownloadButton } from './components/DownloadButton';
import { SpreadsheetLoadingRequestItem } from './loading';

type SpreadsheetRequestItemLoadingProps = {
  isLoading: true;
};

type SpreadsheetRequestItemDataProps = ReturnType<
  typeof normalizeRedeemBatchTicketRequestsData
>[number];

type SpreadsheetRequestItemLoadedProps = SpreadsheetRequestItemDataProps & {
  isLoading?: false;
};

type SpreadsheetRequestItemProps =
  | SpreadsheetRequestItemLoadingProps
  | SpreadsheetRequestItemLoadedProps;

export const SpreadsheetRequestItem: React.FC<SpreadsheetRequestItemProps> = (props) => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { handleDownloadRedeemSpreadsheet, isDownloadingSpreadsheet } = useSpreadsheetRequestItem();
  const { isLoading } = props;

  if (isLoading) {
    return <SpreadsheetLoadingRequestItem />;
  }

  const {
    statusUpdatedAtFormatted,
    fileTypeDescriptionFormatted,
    basketFileType,
    basketFileId,
    basketFileStatus,
    basketFileError,
    basketAdvisorOrderId,
  } = props;

  const downloadButtonIsDisabled = isDownloadingSpreadsheet || basketFileStatus.statusId !== 1001;
  const detailsButtonIsDisabled = basketFileStatus.statusId !== 1002 || !basketAdvisorOrderId;

  const getCorrectButtonProps = () => {
    switch (basketFileType.fileTypeId) {
      case 1:
        return {
          onPress: () => handleDownloadRedeemSpreadsheet(basketFileId),
          isDisabled: downloadButtonIsDisabled,
        };

      case 2:
        return {
          onPress: () =>
            navigate('RedeemBatchTicketDetails', {
              basketFileId,
              basketAdvisorId: basketAdvisorOrderId,
              basketAdvisorStatus: basketFileStatus,
            }),
          isDisabled: detailsButtonIsDisabled,
        };
      default:
        return {
          onPress: () => {},
          isDisabled: true,
        };
    }
  };

  const renderCorrectButton = () => {
    const buttonProps = getCorrectButtonProps();
    switch (basketFileType.fileTypeId) {
      case 1:
        return <DownloadButton {...buttonProps} isDownloading={isDownloadingSpreadsheet} />;
      case 2:
        return <DetailsButton {...buttonProps} />;
      default:
        return null;
    }
  };

  return (
    <Pressable {...getCorrectButtonProps()}>
      <Stack
        direction={{ base: 'column', sm: 'row' }}
        w="full"
        alignItems="center"
        justifyContent="space-between"
        space={{ base: Spacing['inline.nano'], sm: 0 }}
        py={Spacing['stack.xxxs']}>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          w="full"
          maxW={{ base: 'full', sm: '124px' }}
          alignItems={{ sm: 'flex-start', base: 'center' }}
          justifyContent="space-between"
          space={Spacing['stack.nano']}>
          <Text variant={{ base: 'regular', sm: 'semibold' }} color="neutral.black">
            Ult. Atualização
          </Text>
          <Text color={{ base: 'neutral.black', sm: 'neutral.darkest' }}>
            {statusUpdatedAtFormatted}
          </Text>
        </Stack>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          w="full"
          maxW={{ base: 'full', sm: '113px' }}
          alignItems={{ sm: 'flex-start', base: 'center' }}
          justifyContent="space-between"
          space={Spacing['stack.nano']}>
          <Text variant={{ base: 'regular', sm: 'semibold' }} color="neutral.black">
            Planilha
          </Text>
          <Text color={{ base: 'neutral.black', sm: 'neutral.darkest' }}>
            {fileTypeDescriptionFormatted}
          </Text>
        </Stack>
        <Stack
          direction={{ base: 'row', sm: 'column' }}
          w="full"
          maxW={{ sm: '220px', lg: '110px' }}
          alignItems={{ sm: 'flex-start', base: 'center' }}
          justifyContent="space-between"
          space={Spacing['stack.nano']}>
          <Text variant={{ base: 'regular', sm: 'semibold' }} color="neutral.black">
            Status
          </Text>
          <TableContentItemStatus
            maxW={{ sm: '220px', lg: '110px' }}
            customTooltipLabel={basketFileError?.externalMessage || undefined}
            basketAdvisorStatus={basketFileStatus}
          />
        </Stack>
        {renderCorrectButton()}
      </Stack>
    </Pressable>
  );
};
